import { FC, useState, useEffect } from "react"

import { Space } from "antd"

import variables from "@/_styles/variables.module.scss"
import { Table } from "@/components/_Shared/Table"
import { OrderReportValue } from "@/components/Reports/OrderReport/OrderReportValue"
import { Resource } from "@/constants/permission"
import { getOrderReportExcel } from "@/http/orderReport"

import { useOrderReportFilters } from "./OrderReportFilters"

import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useOrderReportQuery } from "@/hook/Dictionaries/useOrderReportQuery"
import { useDownloadFile } from "@/hook/useDownloadFile"
import { useTable } from "@/hook/useTable"

import { DeliveryType } from "@/types/IOrder"
import { ORDER_REPORT_KEY, Period, IOrderReport } from "@/types/IOrderReport"
import { ITableColumn } from "@/types/ui"

import { formatFuelAmount, getMoney } from "@/utils/formatNumber"
import { getInitialColumns } from "@/utils/getInitialColumns"

const OrderReportPage: FC = () => {
  const [activePeriod, setActivePeriod] = useState<Period>(Period.Year)
  const [activeDeliveryType, setActiveDeliveryType] = useState<DeliveryType>()
  const [customerId, setCustomerId] = useState<number>()
  const [isNotShipped, setIsNotShipped] = useState<boolean>(false)

  const { filters } = useOrderReportFilters()

  const style = { color: variables.textblackcolor, fontSize: "14px", fontWeight: 600 }
  const mainColumns: ITableColumn<IOrderReport>[] = [
    {
      title: "Тип топлива",
      key: "fuelTypeName",
      dataIndex: ["fuelTypeName"],
      sorter: true,
      isVisible: true,
      order: 0,
      render: (_, { fuelTypeName }) => 
        <Space>
          {depots &&
            <>
              <span style={style}>{fuelTypeName}</span>
            </>
          }
        </Space>
    }
  ]
  const { data: depots, isLoading: depotsIsLoading } = useDepotSelectListQuery()

  const { columns, pagination, filter, order } = useTable({
    initialColumns: getInitialColumns(mainColumns, ORDER_REPORT_KEY),
    key: ORDER_REPORT_KEY
  })

  const { data, isLoading } = useOrderReportQuery({
    period: activePeriod,
    customerId: customerId,
    deliveryType: activeDeliveryType,
    isNotShipped: isNotShipped == true ? isNotShipped : undefined,
    Page: pagination.page,
    PageSize: pagination.pageSize,
    Filter: filter.filter,
    OrderBy: order.orderBy
  })

  const [tableColumns, setTableColumns] = useState<ITableColumn<IOrderReport>[]>(mainColumns)
  const [orderReports, setOrderReports] = useState<any[] | undefined>([])

  useEffect(()=> {
    if(depots && data?.response.data) {
      tableColumns.length = 0
      mainColumns.map(x => {
        tableColumns.push(x)
      })
      const depotId = filters.find(x => x.name === "depotId")?.value
      depots.response.map(x => {
        if(!depotId || depotId === x.id) {
          tableColumns.push({
            title: x.name,
            key: `depot${x.name}`,
            dataIndex: [`depot${x.name}`],
            sorter: false,
            isVisible: true,
            order: 1,
            render: (_, { depots }) => 
              <Space>
                {depots.find(y => y.depotName === x.name) &&
                  <>
                    <OrderReportValue 
                      orderAmount={depots.find(y => y.depotName === x.name)?.orderAmount as number}
                      orderSum={depots.find(y => y.depotName === x.name)?.orderSum as number}
                    />
                  </>
                }
              </Space>
          })
        }
      })
      
      tableColumns.push({
        title: "Итого",
        key: "depotИтого",
        dataIndex: ["depotИтого"],
        sorter: false,
        isVisible: true,
        order: 1,
        render: (_, { depots }) => 
          <Space>
            {depots &&
              <>
                <OrderReportValue 
                  orderAmount={depots.map(x => x.orderAmount).reduce((a, c) => a + formatFuelAmount(c), 0)}
                  orderSum={depots.map(x => x.orderSum).reduce((a, c) => a + c, 0)}
                />
              </>
            }
          </Space>
      })

      setTableColumns(tableColumns)
      data?.response.data.push({
        fuelTypeName: "Итого",
        depots: depots.response.map(x => ({
          depotId: x.id,
          depotName: x.name,
          orderSum: data?.response.data.flatMap(y => y.depots).filter(y => y.depotName === x.name).map(y => y.orderSum).reduce((a, c) => a + c, 0),
          orderAmount: data?.response.data.flatMap(y => y.depots).filter(y => y.depotName === x.name).map(y => y.orderAmount).reduce((a, c) => a + c, 0)
        }))
      })
      setOrderReports(data?.response.data)
    }
  },[depots, data])

  const { download: excelDownload, isLoading: isExcelLoading } =
    useDownloadFile(() =>
      getOrderReportExcel(
        {
          Filter: filter.filter,
          OrderBy: order.orderBy
        },
        getInitialColumns(tableColumns, ORDER_REPORT_KEY)
      )
    )

  const onExcelDownloadHandler = async () => {
    await excelDownload()
  }

  return (
    <>
      <Table
        tableTitle="Отчет по продажам свободно"
        dataSource={orderReports}
        columns={tableColumns}
        isSelectable={false}
        rowKey="fuelTypeName"
        isLoading={isLoading || tableColumns.length === 1}
        pagination={{
          currentPage: pagination.page,
          pageSize: pagination.pageSize,
          total: data?.response.count,
          onPageChange: pagination.onPageChange,
          onPageSizeChange: pagination.onPageSizeChange
        }}
        onSorterChange={order.setOrderBy}
        download={{
          onDownload: onExcelDownloadHandler,
          isLoading: isExcelLoading
        }}
        filters={{
          filters: filters,
          onSubmit: filter.onFilterChange,
          onCancel: filter.onFilterReset,
          filterKey: ORDER_REPORT_KEY
        }}
        permission={{
          resource: Resource.OrderReport
        }}
      />
    </>
  )
}

export default OrderReportPage
